import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePortfolioStore } from '../../../hooks/usePortfolioStore';
import type { PortfolioItemWithUrl } from '../../../hooks/usePortfolioStore';

const MANAGE_PASSWORD = '0724';
const SESSION_KEY = 'portfolio_manage_auth';

function PasswordGate({ onSuccess }: { onSuccess: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value === MANAGE_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      onSuccess();
    } else {
      setError(true);
      setShake(true);
      setValue('');
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-stone-100 p-8 w-full max-w-sm">
        <div className="flex flex-col items-center mb-7">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-stone-100 mb-4">
            <i className="ri-lock-line text-2xl text-stone-500" />
          </div>
          <h1 className="text-stone-800 font-bold text-lg">관리자 전용</h1>
          <p className="text-stone-400 text-sm mt-1">비밀번호를 입력해 주세요</p>
        </div>

        <form onSubmit={handleSubmit} className={`space-y-4 ${shake ? 'animate-[wiggle_0.4s_ease-in-out]' : ''}`}>
          <style>{`@keyframes wiggle { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }`}</style>
          <div>
            <input
              ref={inputRef}
              type="password"
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(false); }}
              placeholder="비밀번호"
              className={`w-full px-4 py-3 rounded-xl border text-sm text-stone-800 outline-none transition-colors ${
                error ? 'border-red-300 bg-red-50' : 'border-stone-200 bg-stone-50 focus:border-stone-400'
              }`}
            />
            {error && (
              <p className="mt-1.5 text-xs text-red-500">비밀번호가 틀렸습니다.</p>
            )}
          </div>
          <button
            type="submit"
            className="w-full py-3 bg-stone-900 hover:bg-stone-700 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            확인
          </button>
        </form>

        <div className="mt-5 text-center">
          <Link
            to="/portfolio"
            className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
          >
            ← 포트폴리오로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

interface PendingFile {
  file: File;
  previewUrl: string;
}

export default function PortfolioManagePage() {
  const { items, loading, addItems, deleteItem } = usePortfolioStore();
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [authenticated, setAuthenticated] = useState(() =>
    sessionStorage.getItem(SESSION_KEY) === 'true'
  );

  useEffect(() => {
    // noindex for admin/manage page
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    meta.id = 'manage-robots';
    document.head.appendChild(meta);

    const prevTitle = document.title;
    document.title = '사진 관리 | 라인 인테리어';

    return () => {
      document.getElementById('manage-robots')?.remove();
      document.title = prevTitle;
    };
  }, []);

  const handleFiles = useCallback((files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    const newPending = imageFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPending((prev) => [...prev, ...newPending]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const removePending = (index: number) => {
    setPending((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (pending.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      await addItems(pending.map((p) => p.file));
      pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      setPending([]);
    } catch {
      setUploadError('업로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteItem(id);
    setDeleteConfirm(null);
  };

  if (!authenticated) {
    return <PasswordGate onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Top Bar */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/portfolio"
              className="flex items-center gap-1.5 text-sm text-stone-400 hover:text-stone-700 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              포트폴리오
            </Link>
            <span className="text-stone-200">|</span>
            <h1 className="text-stone-800 font-semibold text-sm">사진 관리</h1>
          </div>
          <span className="text-xs text-stone-400 bg-stone-100 px-3 py-1 rounded-full">
            {items.length}장 등록됨
          </span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-8">

        {/* Upload Zone */}
        <div>
          <h2 className="text-base font-semibold text-stone-800 mb-3">사진 추가</h2>
          <div
            className={`relative border-2 border-dashed rounded-2xl transition-all duration-200 cursor-pointer ${
              isDragging
                ? 'border-stone-500 bg-stone-100'
                : 'border-stone-200 hover:border-stone-400 bg-white'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-stone-100 mb-4">
                <i className="ri-upload-cloud-2-line text-2xl text-stone-400" />
              </div>
              <p className="text-stone-700 font-medium text-sm mb-1">
                사진을 여기에 드래그하거나 클릭해서 선택
              </p>
              <p className="text-stone-400 text-xs">여러 장 한번에 선택 가능 · JPG, PNG, WEBP</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files) handleFiles(e.target.files); }}
            />
          </div>

          {/* Pending Preview */}
          {pending.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-stone-600 font-medium">
                  업로드 대기 중 <span className="text-stone-400">{pending.length}장</span>
                </p>
                <button
                  onClick={() => {
                    pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
                    setPending([]);
                  }}
                  className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  전체 취소
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-4">
                {pending.map((p, idx) => (
                  <div key={p.previewUrl} className="relative group aspect-square rounded-lg overflow-hidden">
                    <img
                      src={p.previewUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); removePending(idx); }}
                      className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-black/60 hover:bg-red-500 text-white rounded-full cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full py-3 bg-stone-900 hover:bg-stone-700 disabled:bg-stone-300 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {uploading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    업로드 중...
                  </span>
                ) : `${pending.length}장 포트폴리오에 추가`}
              </button>
              {uploadError && (
                <p className="mt-2 text-xs text-red-500 text-center">{uploadError}</p>
              )}
            </div>
          )}
        </div>

        {/* Registered Photos */}
        <div>
          <h2 className="text-base font-semibold text-stone-800 mb-3">
            등록된 사진
            <span className="text-stone-400 text-sm font-normal ml-2">{items.length}장</span>
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-20 bg-white rounded-2xl border border-stone-100">
              <div className="w-6 h-6 border-2 border-stone-200 border-t-stone-600 rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-stone-100">
              <i className="ri-image-2-line text-3xl text-stone-200 mb-3" />
              <p className="text-stone-400 text-sm">아직 등록된 사진이 없습니다</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {items.map((item: PortfolioItemWithUrl) => (
                <div
                  key={item.id}
                  className="relative group aspect-square rounded-lg overflow-hidden bg-stone-100"
                >
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {deleteConfirm === item.id ? (
                    <div className="absolute inset-0 bg-black/75 flex flex-col items-center justify-center gap-2 p-2">
                      <p className="text-white text-xs text-center">삭제할까요?</p>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="px-2.5 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-md cursor-pointer transition-colors whitespace-nowrap"
                        >
                          삭제
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-2.5 py-1 bg-white/20 hover:bg-white/30 text-white text-xs rounded-md cursor-pointer transition-colors whitespace-nowrap"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(item.id)}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/50 hover:bg-red-500 text-white rounded-full cursor-pointer transition-all duration-200 opacity-0 group-hover:opacity-100"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
